import { BreadType } from 'types/bread'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useStringChangeEvent } from 'hooks/form'
import { StoreType } from 'types/store'
import { filterBreadsByKeyword, filterStoresByKeyword } from 'lib/filter'
import { fetchBreads } from 'repos/bread'
import { fetchStores } from 'repos/store'

type UseSearchBreads = {
  keyword: string
  handleKeyword: (event: ChangeEvent<HTMLInputElement>) => void
  searchedBreads: BreadType[]
  addedBreads: BreadType[]
  searchBreads: () => void
}

const useSearchBreads = (breadIds: string[]): UseSearchBreads => {
  const [keyword, setKeyword] = useState<string>('')
  const [searchedBreads, setSearchedBreads] = useState<BreadType[]>([])
  const [addedBreads, setAddedBreads] = useState<BreadType[]>([])
  const [breads, setBreads] = useState<BreadType[]>([])
  const [stores, setStores] = useState<StoreType[]>([])

  const handleKeyword = useStringChangeEvent(setKeyword)

  const searchBreads = useCallback(async (): Promise<void> => {
    try {
      const filteredBreads = filterBreadsByKeyword(keyword, breads)
      setSearchedBreads([...filteredBreads])
      if (filteredBreads.length > 0) return
      // 商品名でヒットしなかった場合、店舗名で検索し、ヒットした店舗の商品を全て表示
      const filteredStores = filterStoresByKeyword(keyword, stores)
      if (filteredStores.length === 0) {
        setSearchedBreads([])
      } else {
        const breadsOfStores = breads.filter((bread) =>
          filteredStores.some((store) => store.shop_ref?.id === bread.shop_ref.id)
        )
        setSearchedBreads([...breadsOfStores])
      }
    } catch (e) {
      console.error(e)
    }
  }, [keyword, setSearchedBreads, breads, stores])

  // 検索キーワードが削除されたら検索結果も初期化
  useEffect(() => {
    if (keyword !== '') return
    setSearchedBreads([])
  }, [keyword])

  useEffect(() => {
    ;(async () => {
      const _breads = await fetchBreads(['public'])
      setBreads([..._breads])
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      const _stores = await fetchStores(['public', 'private'])
      setStores([..._stores])
    })()
  }, [])

  // 商品が追加されたら商品情報を取得
  useEffect(() => {
    ;(async () => {
      if (breadIds.length === 0) {
        setAddedBreads([])
      } else {
        try {
          const nextBreads = breads.filter((bread) => breadIds.includes(bread.bread_ref?.id || ''))
          setAddedBreads([...nextBreads])
        } catch (e) {
          console.error(e)
        }
      }
    })()
  }, [breadIds, breads])

  return {
    keyword,
    handleKeyword,
    searchedBreads,
    addedBreads,
    searchBreads,
  }
}

export default useSearchBreads
